/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */
/** @jsx jsx */

import React from 'react'
import { css, jsx } from '@emotion/react'

import { EuiPanel, useCurrentEuiBreakpoint } from '@elastic/eui'

import PromptView from './PromptView'
import SentFeedbackView from './SentFeedbackView'

import type { FeedbackType, FeedbackView } from './types'

const getPanelStyles = (isMobile: boolean) =>
  css({
    position: 'fixed',
    zIndex: 1001,
    ...(isMobile
      ? {
          left: '50%',
          transform: 'translate(-50%, 50%)',
          right: 'auto',
          bottom: 96,
        }
      : {
          right: 32,
          maxWidth: 310,
          bottom: 32,
        }),
  })
interface FeedbackPanelProps {
  feedbackSnippetId: string
  feedbackView: FeedbackView
  promptViewMessage: React.ReactNode
  onDismissPanel: () => void
  onFeedback: (feedback: FeedbackType) => void
}

/**
 * A panel to gather user feedback.
 * There are 3 available views:
 * - Prompt: Ask the user for feedback
 * - Positive
 * - Negative
 */
const FeedbackPanel = ({
  feedbackSnippetId,
  feedbackView,
  promptViewMessage,
  onDismissPanel,
  onFeedback,
}: FeedbackPanelProps) => {
  const isMobile = useCurrentEuiBreakpoint() === 'xs'
  const isPromptView = feedbackView === 'prompt'

  return (
    <EuiPanel
      data-test-subj='feedbackSnippetPanel'
      grow={false}
      hasShadow={true}
      paddingSize='l'
      css={getPanelStyles(isMobile)}
    >
      {isPromptView ? (
        <PromptView
          feedbackSnippetId={feedbackSnippetId}
          promptViewMessage={promptViewMessage}
          onDismissPanel={onDismissPanel}
          onFeedback={onFeedback}
        />
      ) : (
        <SentFeedbackView />
      )}
    </EuiPanel>
  )
}

export default FeedbackPanel
